/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState } from 'react'

import { Link, useParams } from 'react-router-dom'

import { Alert, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'

import { MigrationFiltersBar } from './MigrationFiltersBar'
import { RunHistory } from './RunHistory'
import { RunPanel } from './RunPanel'
import { JiraLink, StatusBadge } from './StatusBadge'
import { MIGRATION_STATUSES, type MigrationItem, type MigrationItemDetail, type MigrationStatus } from './types'
import { useMigrationFilters } from './useMigrationFilters'

import { LoadingPanel } from '../LoadingPanel'

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="btn btn-ghost btn-xs"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

const needsText = (item: MigrationItem) =>
  [item.needs.company && 'company', item.needs.asset && 'asset', item.needs.dates && 'date range']
    .filter(Boolean)
    .join(', ') || 'nothing'

/**
 * One endpoint or issue: what it is, its status and Jira ticket (editable), a re-runnable
 * comparison of both back ends, and the SQL pairs that show the underlying data.
 */
const MigrationItemPage: React.FC = () => {
  const { api } = useApi()
  const { id = '' } = useParams<{ id: string }>()
  const { filters, setFilters, search } = useMigrationFilters()
  const [item, setItem] = useState<MigrationItemDetail | null>(null)
  const [error, setError] = useState('')
  const [jiraKey, setJiraKey] = useState('')
  const [status, setStatus] = useState<MigrationStatus>('todo')
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [historyKey, setHistoryKey] = useState(0)
  const refreshHistory = () => setHistoryKey((k) => k + 1)

  useEffect(() => {
    const load = async () => {
      const res = await api.get<MigrationItemDetail>({
        path: `/app/admin/migration/items/${encodeURIComponent(id)}`,
        origin: 'MigrationItemPage#load',
      })
      if (res.error || !res.data) {
        setError(res.message || 'Could not load this item')
        return
      }
      setItem(res.data)
      setJiraKey(res.data.jiraKey ?? '')
      setStatus(res.data.status)
    }
    void load()
  }, [api, id])

  const save = async () => {
    setSaving(true)
    setSaveMessage('')
    const res = await api.put<MigrationItem, { status: MigrationStatus; jiraKey: string }>({
      path: `/app/admin/migration/items/${encodeURIComponent(id)}`,
      body: { status, jiraKey },
      origin: 'MigrationItemPage#save',
    })
    setSaving(false)
    if (res.error || !res.data) {
      setSaveMessage(res.message || 'Could not save')
      return
    }
    const saved = res.data
    setItem((prev) => (prev ? { ...prev, ...saved } : prev))
    setJiraKey(saved.jiraKey ?? '')
    setSaveMessage('Saved')
  }

  if (error) {
    return (
      <PageContainer>
        <Alert message={error} type="error" />
      </PageContainer>
    )
  }
  if (!item) {
    return (
      <PageContainer>
        <LoadingPanel label="Loading…" />
      </PageContainer>
    )
  }

  const dirty = status !== item.status || jiraKey.trim().toUpperCase() !== (item.jiraKey ?? '')

  return (
    <PageContainer>
      <Link to={`/admin/migration?${search}`} className="link text-sm">
        ← Migration Dashboard
      </Link>
      <PageHeader title={item.name} subtitle={item.kind === 'endpoint' ? (item.path ?? '') : (item.ticket ?? '')} />

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="card bg-base-100 shadow">
          <div className="card-body gap-2 text-sm">
            <h2 className="card-title text-base">{item.kind === 'endpoint' ? 'Endpoint' : 'Data issue'}</h2>
            {item.description && <p>{item.description}</p>}
            {item.kind === 'endpoint' && (
              <>
                <div>
                  <span className="font-semibold">Timescale:</span> <code>{item.path}</code>{' '}
                  {item.converted ? '' : <span className="text-base-content/60">(still a Fresh alias)</span>}
                </div>
                <div>
                  <span className="font-semibold">Fresh:</span> <code>{item.freshPath}</code>
                </div>
              </>
            )}
            <div>
              <span className="font-semibold">Needs:</span> {needsText(item)}
            </div>
            {item.compare && (
              <div>
                <span className="font-semibold">Compared on:</span> {item.compare.key?.join(', ') ?? 'whole rows'}
                {item.compare.ignore?.length ? `; ignoring ${item.compare.ignore.join(', ')}` : ''}
                {item.compare.knownGaps
                  ? `; known gaps ${Object.entries(item.compare.knownGaps)
                      .map(([f, t]) => `${f} (${t})`)
                      .join(', ')}`
                  : ''}
              </div>
            )}
            {item.tickets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="font-semibold">Related:</span>
                {item.tickets.map((t) => (
                  <JiraLink key={t} jiraKey={t} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body gap-3 text-sm">
            <h2 className="card-title text-base">
              Status <StatusBadge status={item.status} />
            </h2>
            <label className="form-control">
              <span className="label-text">Status</span>
              <select
                className="select select-bordered select-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as MigrationStatus)}
              >
                {MIGRATION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-control">
              <span className="label-text">Jira ticket</span>
              <div className="flex items-center gap-2">
                <input
                  className="input input-bordered input-sm w-40"
                  placeholder="MEGA-734"
                  value={jiraKey}
                  onChange={(e) => setJiraKey(e.target.value)}
                />
                <JiraLink jiraKey={item.jiraKey} />
              </div>
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={!dirty || saving}
                onClick={() => void save()}
              >
                Save
              </button>
              {saveMessage && <span className="text-base-content/70">{saveMessage}</span>}
              {item.updatedBy && <span className="text-xs text-base-content/50">last set by {item.updatedBy}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <MigrationFiltersBar filters={filters} onChange={setFilters} />
      </div>

      {item.kind === 'endpoint' && item.status !== 'todo' && (
        <section className="card mt-4 bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-base">API comparison</h2>
            <RunPanel
              itemId={item.id}
              target={{ kind: 'test' }}
              filters={filters}
              search={search}
              needsAsset={item.needs.asset}
              compare={item.compare}
              label="Run test"
              onRun={refreshHistory}
            />
          </div>
        </section>
      )}

      {item.sql.length > 0 && <h2 className="mt-6 mb-2 text-lg font-semibold">SQL</h2>}
      {item.sql.map((pair) => (
        <section key={pair.id} className="card mb-4 bg-base-100 shadow" data-testid={`sql-${pair.id}`}>
          <div className="card-body gap-3">
            <h3 className="card-title text-base">{pair.title}</h3>
            <p className="text-sm">{pair.explains}</p>
            <p className="text-xs text-base-content/60">
              Parameters: <code>@companyId</code>
              {pair.needsAsset && (
                <>
                  , <code>@assetId</code> (Fresh AssetId = <code>asset.legacy_id</code>)
                </>
              )}
              , <code>@from</code>/<code>@to</code> in UTC.
            </p>
            <div className="grid gap-4 xl:grid-cols-2">
              {(['fresh', 'timescale'] as const).map((side) => (
                <div key={side}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">
                      {side === 'fresh' ? 'Fresh (SQL Server)' : 'Timescale (Postgres)'}
                    </span>
                    <CopyButton text={pair[side]} />
                  </div>
                  <pre className="max-h-80 overflow-auto rounded bg-base-200 p-3 text-xs">{pair[side]}</pre>
                </div>
              ))}
            </div>
            <RunPanel
              itemId={item.id}
              target={{ kind: 'sql', sqlId: pair.id }}
              filters={filters}
              search={search}
              needsAsset={pair.needsAsset}
              compare={pair.compare}
              label="Run SQL"
              onRun={refreshHistory}
            />
          </div>
        </section>
      ))}

      <section className="card mt-2 mb-4 bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-base">Run history</h2>
          <RunHistory itemId={item.id} sql={item.sql} refreshKey={historyKey} onUseFilters={setFilters} />
        </div>
      </section>
    </PageContainer>
  )
}

export default MigrationItemPage
